import React, { useState } from "react";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useGetFeederGroupsQuery } from "../UserSettings/scheduleApiSlice";
import { getToken } from "../api/apiHelper";
import config from "../../config";

// Window offsets in minutes relative to switch-on time
const WINDOWS = [
  { label: "PRE",      desc: "5 min before first switch-on", startMin: -5, endMin: 0,  color: "#2E93FA",              bg: "rgba(46,147,250,0.15)",   showStart: true  },
  { label: "NOTCH",   desc: "2 min after",                  startMin: 0,  endMin: 2,  color: "#FDB940",              bg: "rgba(253,185,64,0.15)",   showStart: false },
  { label: "SKIP",    desc: "Skip 1 min",                   startMin: 2,  endMin: 3,  color: "rgba(255,255,255,0.5)", bg: "rgba(255,255,255,0.08)",  showStart: false },
  { label: "PERIOD 1", desc: "5 min",                       startMin: 3,  endMin: 8,  color: "#FDB940",              bg: "rgba(253,185,64,0.15)",   showStart: false },
  { label: "PERIOD 2", desc: "5 min",                       startMin: 8,  endMin: 13, color: "#FDB940",              bg: "rgba(253,185,64,0.15)",   showStart: false },
  { label: "PERIOD 3", desc: "5 min",                       startMin: 13, endMin: 18, color: "#FDB940",              bg: "rgba(253,185,64,0.15)",   showStart: false },
  { label: "PERIOD 4", desc: "5 min",                       startMin: 18, endMin: 23, color: "#FDB940",              bg: "rgba(253,185,64,0.15)",   showStart: false },
  { label: "PERIOD 5", desc: "5 min",                       startMin: 23, endMin: 28, color: "#FDB940",              bg: "rgba(253,185,64,0.15)",   showStart: false },
];

function fmtTime(d) {
  if (!d || !d.isValid()) return "—";
  return d.format("HH:mm:ss");
}

const darkInputSx = {
  "& .MuiOutlinedInput-root": {
    color: "#e0e6f0",
    bgcolor: "rgba(255,255,255,0.04)",
    "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.25)" },
    "&.Mui-focused fieldset": { borderColor: "rgba(253,185,64,0.5)" },
  },
  "& .MuiInputBase-input": { color: "#e0e6f0", fontSize: "0.88rem" },
  "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.45)" },
};

const fieldLabelSx = {
  fontSize: "0.72rem",
  fontWeight: 700,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.4)",
  mb: "8px",
};

const helperTextSx = {
  fontSize: "0.75rem",
  color: "rgba(255,255,255,0.35)",
  mt: "6px",
};

function parseCsv(text) {
  const lines = text.trim().split("\n");
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = lines[0].split(",").map((h) => h.trim());
  const rows = lines.slice(1).map((line) =>
    line.split(",").map((cell) => cell.trim())
  );
  return { headers, rows };
}

export default function NotchExportPage() {
  const [switchOnTime, setSwitchOnTime] = useState(
    dayjs().second(0).millisecond(0)
  );
  const [feederGroupId, setFeederGroupId] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  const { data: feederGroups, isLoading: fgLoading } = useGetFeederGroupsQuery();

  const windows = WINDOWS.map((w) => ({
    ...w,
    start: switchOnTime?.isValid()
      ? switchOnTime.add(w.startMin, "minute")
      : null,
    end: switchOnTime?.isValid()
      ? switchOnTime.add(w.endMin, "minute")
      : null,
  }));

  const buildParams = () => {
    const params = new URLSearchParams();
    params.set("switch_on_time", switchOnTime.toISOString());
    if (feederGroupId) params.set("feeder_group_id", String(feederGroupId));
    return params;
  };

  const handleExport = async () => {
    if (!switchOnTime?.isValid()) return;
    setIsExporting(true);
    setExportError(null);
    try {
      const response = await fetch(
        `${config.REACT_APP_SECRET_API}/dsm/notch-export/?${buildParams().toString()}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `notch_${switchOnTime.format("YYYY-MM-DD_HH-mm-ss")}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err.message || "Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  const handlePreview = async () => {
    if (!switchOnTime?.isValid()) return;
    setIsPreviewing(true);
    setPreviewError(null);
    setPreviewData(null);
    try {
      const response = await fetch(
        `${config.REACT_APP_SECRET_API}/dsm/notch-export/?${buildParams().toString()}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `HTTP ${response.status}`);
      }
      const text = await response.text();
      setPreviewData(parseCsv(text));
    } catch (err) {
      setPreviewError(err.message || "Preview failed");
    } finally {
      setIsPreviewing(false);
    }
  };

  return (
    <Box sx={{ padding: "70px 32px 40px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Page header */}
      <Box sx={{ pt: "28px", mb: "28px" }}>
        <Typography
          sx={{ fontSize: "1.7rem", fontWeight: 700, color: "#fff", letterSpacing: "-0.025em", lineHeight: 1.2 }}
        >
          Notch Export
        </Typography>
        <Typography sx={{ fontSize: "0.88rem", color: "rgba(255,255,255,0.45)", mt: "4px" }}>
          Phase 1 · operator self-service · raw-data export in M&V spreadsheet layout (NT-02..06)
        </Typography>
      </Box>

      {/* ── Single card: form (left) + windows (right) ── */}
      <Paper
        elevation={0}
        sx={{
          backgroundColor: "#161B22",
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: "12px",
          p: "28px",
        }}
      >
        {/* Card title + subtitle */}
        <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff", mb: "4px" }}>
          Pick a notch event
        </Typography>
        <Typography sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.4)", mb: "28px", maxWidth: 680 }}>
          Choose the exact moment of switch-on (SAST). The portal pulls raw data in the fixed windows shown below, in the column layout the M&amp;V macro expects.
        </Typography>

        {/* Two-column layout */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(280px, 400px) 1fr",
            gap: "48px",
            alignItems: "start",
          }}
        >
          {/* ── Left: form ── */}
          <Box>
            {/* Switch-on time */}
            <Box sx={{ mb: "20px" }}>
              <Typography sx={fieldLabelSx}>Notch switch-on time (SAST · UTC+2)</Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  value={switchOnTime}
                  onChange={(v) =>
                    setSwitchOnTime(v ? v.second(0).millisecond(0) : null)
                  }
                  ampm={false}
                  format="YYYY/MM/DD HH:mm"
                  slotProps={{
                    textField: { size: "small", fullWidth: true, sx: darkInputSx },
                  }}
                />
              </LocalizationProvider>
              <Typography sx={helperTextSx}>24-hour format · seconds default to 00</Typography>
            </Box>

            {/* Feeder group */}
            <Box sx={{ mb: "28px" }}>
              <Typography sx={fieldLabelSx}>Feeder group (optional)</Typography>
              <FormControl size="small" fullWidth sx={darkInputSx}>
                <Select
                  value={feederGroupId}
                  onChange={(e) => setFeederGroupId(e.target.value)}
                  displayEmpty
                  sx={{
                    color: "#e0e6f0",
                    "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.45)" },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: "#1c2330",
                        border: "1px solid rgba(255,255,255,0.1)",
                        "& .MuiMenuItem-root": {
                          color: "#e0e6f0",
                          fontSize: "0.88rem",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.05)" },
                          "&.Mui-selected": { bgcolor: "rgba(253,185,64,0.1)", color: "#FDB940" },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">All enrolled feeders</MenuItem>
                  {fgLoading ? (
                    <MenuItem disabled>Loading…</MenuItem>
                  ) : (
                    (feederGroups || []).map((fg) => (
                      <MenuItem key={fg.id} value={fg.id}>
                        {fg.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              <Typography sx={helperTextSx}>
                Filter the export to a specific feeder group — defaults to all.
              </Typography>
            </Box>

            {/* Buttons */}
            <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <Button
                variant="contained"
                startIcon={
                  isExporting ? (
                    <CircularProgress size={14} sx={{ color: "#000" }} />
                  ) : (
                    <FileDownloadIcon />
                  )
                }
                onClick={handleExport}
                disabled={isExporting || isPreviewing || !switchOnTime?.isValid()}
                sx={{
                  bgcolor: "#FDB940",
                  color: "#000",
                  fontWeight: 700,
                  fontSize: "0.82rem",
                  textTransform: "none",
                  borderRadius: "8px",
                  px: "20px",
                  "&:hover": { bgcolor: "#e5a830" },
                  "&:disabled": {
                    bgcolor: "rgba(253,185,64,0.25)",
                    color: "rgba(0,0,0,0.4)",
                  },
                }}
              >
                {isExporting ? "Exporting…" : "Export CSV"}
              </Button>

              <Button
                variant="outlined"
                startIcon={
                  isPreviewing ? (
                    <CircularProgress size={14} sx={{ color: "#FDB940" }} />
                  ) : (
                    <VisibilityIcon />
                  )
                }
                onClick={handlePreview}
                disabled={isExporting || isPreviewing || !switchOnTime?.isValid()}
                sx={{
                  borderColor: "rgba(253,185,64,0.5)",
                  color: "#FDB940",
                  fontWeight: 600,
                  fontSize: "0.82rem",
                  textTransform: "none",
                  borderRadius: "8px",
                  px: "20px",
                  "&:hover": { borderColor: "#FDB940", bgcolor: "rgba(253,185,64,0.06)" },
                  "&:disabled": {
                    borderColor: "rgba(255,255,255,0.12)",
                    color: "rgba(255,255,255,0.25)",
                  },
                }}
              >
                {isPreviewing ? "Loading…" : "Preview"}
              </Button>
            </Box>

            {exportError && (
              <Typography sx={{ mt: "12px", fontSize: "0.78rem", color: "#e05050", lineHeight: 1.5 }}>
                {exportError}
              </Typography>
            )}
            {previewError && (
              <Typography sx={{ mt: "12px", fontSize: "0.78rem", color: "#e05050", lineHeight: 1.5 }}>
                {previewError}
              </Typography>
            )}
          </Box>

          {/* ── Right: window boundaries ── */}
          <Box>
            <Typography
              sx={{
                fontSize: "0.68rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.3)",
                mb: "16px",
              }}
            >
              Fixed windows · raw extraction
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              {windows.map((w) => (
                <Box
                  key={w.label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    py: "9px",
                    px: "12px",
                    bgcolor: "rgba(255,255,255,0.02)",
                    border: "1px solid rgba(255,255,255,0.06)",
                    borderRadius: "8px",
                  }}
                >
                  {/* Badge */}
                  <Typography
                    component="span"
                    sx={{
                      px: "6px",
                      py: "2px",
                      borderRadius: "4px",
                      bgcolor: w.bg,
                      color: w.color,
                      fontSize: "0.68rem",
                      fontWeight: 600,
                      letterSpacing: "0.04em",
                      flexShrink: 0,
                      minWidth: "58px",
                      textAlign: "center",
                    }}
                  >
                    {w.label}
                  </Typography>

                  {/* Description */}
                  <Typography
                    sx={{
                      flex: 1,
                      fontSize: "0.82rem",
                      color: "rgba(255,255,255,0.55)",
                    }}
                  >
                    {w.desc}
                  </Typography>

                  {/* Time */}
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      color: "#e0e6f0",
                      fontFamily: "'JetBrains Mono', monospace",
                      fontWeight: 500,
                      fontVariantNumeric: "tabular-nums",
                      flexShrink: 0,
                    }}
                  >
                    {w.showStart ? fmtTime(w.start) : fmtTime(w.end)}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Typography
              sx={{
                mt: "16px",
                fontSize: "0.75rem",
                color: "rgba(255,255,255,0.3)",
                lineHeight: 1.6,
              }}
            >
              Window boundaries are derived from the chosen switch-on time. The macro consumes them as{" "}
              <Box component="strong" sx={{ color: "rgba(255,255,255,0.5)" }}>
                numeric
              </Box>{" "}
              time values, not text.
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* ── Data preview table ── */}
      {previewData && (
        <Paper
          elevation={0}
          sx={{
            mt: "24px",
            backgroundColor: "#161B22",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: "12px",
            p: "24px",
            overflow: "auto",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "16px" }}>
            <Box>
              <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: "#fff", mb: "2px" }}>
                Data preview
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.4)" }}>
                {previewData.rows.length} rows · first 50 shown · click Export CSV to download full dataset
              </Typography>
            </Box>
            <Button
              size="small"
              onClick={() => setPreviewData(null)}
              sx={{ color: "rgba(255,255,255,0.35)", fontSize: "0.75rem", textTransform: "none", minWidth: 0 }}
            >
              Dismiss
            </Button>
          </Box>

          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "0.78rem" }}>
            <Box component="thead">
              <Box component="tr">
                {previewData.headers.map((h) => (
                  <Box
                    component="th"
                    key={h}
                    sx={{
                      textAlign: "left",
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      letterSpacing: "0.09em",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.3)",
                      pb: "10px",
                      pr: "24px",
                      borderBottom: "1px solid rgba(255,255,255,0.07)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {previewData.rows.slice(0, 50).map((row, ri) => (
                <Box component="tr" key={ri}>
                  {row.map((cell, ci) => (
                    <Box
                      component="td"
                      key={ci}
                      sx={{
                        py: "6px",
                        pr: "24px",
                        color: "#e0e6f0",
                        fontFamily: "monospace",
                        fontSize: "0.78rem",
                        borderBottom: "1px solid rgba(255,255,255,0.03)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {cell}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
}
